import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import apiClient from "utils/apiClient";

const TRACKS = ["volume", "schedule", "streak", "ingredient", "time", "xp"];
const GOAL_TYPES = [
  "total_recipes_cooked",
  "total_schedules_completed",
  "reach_xp",
  "reach_streak",
  "cook_ingredient",
  "cook_under_minutes",
];

const TRACK_COLORS = {
  volume: { bg: "#eef0fb", color: "#5c6bc0" },
  schedule: { bg: "#e0f4fd", color: "#0ea5e9" },
  streak: { bg: "#fef3c7", color: "#f59e0b" },
  ingredient: { bg: "#d1fae5", color: "#10b981" },
  time: { bg: "#ede9fe", color: "#8b5cf6" },
  xp: { bg: "#fee2e2", color: "#ef4444" },
};

const TH = ({ children, align }) => (
  <th
    style={{
      padding: "10px 14px",
      textAlign: align || "left",
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#6b7280",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderBottom: "1px solid #f0f0f0",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

TH.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TH.defaultProps = { align: "left" };

const TD = ({ children, align }) => (
  <td
    style={{
      padding: "12px 14px",
      textAlign: align || "left",
      borderBottom: "1px solid #f7f7f7",
      fontSize: "0.83rem",
      color: "#374151",
      verticalAlign: "middle",
    }}
  >
    {children}
  </td>
);

TD.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TD.defaultProps = { align: "left" };

function LabeledField({ label, children }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: "#6b7280", fontWeight: 600, mb: 0.5, display: "block" }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

LabeledField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.83rem" } };

function AdminGoals() {
  const [goals, setGoals] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);

  const takenPositions = editTarget
    ? goals
        .filter((g) => g.track === form.track && g.id !== editTarget.id && g.position != null)
        .map((g) => g.position)
        .sort((a, b) => a - b)
    : [];
  const positionConflict =
    form.position !== "" && form.position != null && takenPositions.includes(Number(form.position));

  useEffect(() => {
    apiClient
      .get("/api/admin/goals")
      .then((r) => setGoals(r.data))
      .catch(() => setError("Failed to load goals"));
  }, []);

  const openEdit = (g) => {
    setEditTarget(g);
    setForm({
      title: g.title,
      description: g.description,
      target: g.target,
      xp: g.xp,
      icon: g.icon || "",
      position: g.position ?? "",
      track: g.track || "volume",
      goal_type: g.goal_type || "total_recipes_cooked",
      target_type: g.target_type || "",
      param: g.param || "",
    });
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = () => {
    const params = {};
    const str = (k) => form[k] !== (editTarget[k] ?? "") && (params[k] = form[k] || null);
    const num = (k) => Number(form[k]) !== editTarget[k] && (params[k] = Number(form[k]));

    if (form.title !== editTarget.title) params.title = form.title;
    str("description");
    num("target");
    num("xp");
    str("icon");
    if (form.position !== "" && Number(form.position) !== editTarget.position)
      params.position = Number(form.position);
    if (form.track !== editTarget.track) params.track = form.track;
    if (form.goal_type !== editTarget.goal_type) params.goal_type = form.goal_type;
    str("target_type");
    str("param");

    apiClient
      .patch(`/api/admin/goals/${editTarget.id}`, null, { params })
      .then(() => {
        setGoals((prev) =>
          prev.map((g) =>
            g.id === editTarget.id
              ? { ...g, ...form, target: Number(form.target), xp: Number(form.xp) }
              : g
          )
        );
        setEditTarget(null);
      })
      .catch(() => alert("Failed to save goal"));
  };

  const handleDelete = (goalId, title) => {
    if (!window.confirm(`Delete goal "${title}"? This cannot be undone.`)) return;
    apiClient
      .delete(`/api/admin/goals/${goalId}`)
      .then(() => setGoals((prev) => prev.filter((g) => g.id !== goalId)))
      .catch(() => alert("Failed to delete goal"));
  };

  if (error)
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#6b7280", mb: 2.5 }}>
        {goals.length} total
      </Typography>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>Title</TH>
              <TH>Track</TH>
              <TH>Goal Type</TH>
              <TH>Target</TH>
              <TH>XP</TH>
              <TH>Pos</TH>
              <TH align="center">Actions</TH>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => {
              const tc = TRACK_COLORS[g.track] || { bg: "#f3f4f6", color: "#6b7280" };
              return (
                <tr key={g.id}>
                  <TD>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                      {g.title}
                    </Typography>
                    {g.param && (
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                        param: {g.param}
                      </Typography>
                    )}
                  </TD>
                  <TD>
                    <Chip
                      label={g.track}
                      size="small"
                      sx={{ bgcolor: tc.bg, color: tc.color, fontWeight: 600, fontSize: "0.7rem" }}
                    />
                  </TD>
                  <TD>
                    <Typography
                      variant="caption"
                      sx={{ color: "#6b7280", fontFamily: "monospace" }}
                    >
                      {g.goal_type}
                    </Typography>
                  </TD>
                  <TD>{g.target}</TD>
                  <TD>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#8b5cf6" }}>
                      {g.xp} XP
                    </Typography>
                  </TD>
                  <TD>{g.position ?? <span style={{ color: "#d1d5db" }}>—</span>}</TD>
                  <TD align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(g)}
                          sx={{
                            color: "#9ca3af",
                            "&:hover": { color: "#5c6bc0", bgcolor: "#eef0fb" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(g.id, g.title)}
                          sx={{
                            color: "#d1d5db",
                            "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>
          Edit — {editTarget?.title}
        </DialogTitle>

        <DialogContent sx={{ pt: "12px !important" }}>
          <Grid container spacing={2}>
            {/* Row 1: title + icon */}
            <Grid item xs={8}>
              <LabeledField label="Title">
                <TextField
                  fullWidth
                  size="small"
                  value={form.title ?? ""}
                  onChange={set("title")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={4}>
              <LabeledField label="Icon">
                <TextField
                  fullWidth
                  size="small"
                  value={form.icon ?? ""}
                  onChange={set("icon")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>

            {/* Row 2: description */}
            <Grid item xs={12}>
              <LabeledField label="Description">
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={form.description ?? ""}
                  onChange={set("description")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>

            {/* Row 3: target, xp, position */}
            <Grid item xs={4}>
              <LabeledField label="Target">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.target ?? ""}
                  onChange={set("target")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={4}>
              <LabeledField label="XP">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.xp ?? ""}
                  onChange={set("xp")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={4}>
              <LabeledField label="Position">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.position ?? ""}
                  onChange={set("position")}
                  error={positionConflict}
                  helperText={
                    positionConflict
                      ? `Position ${form.position} is taken`
                      : takenPositions.length
                      ? `Taken in ${form.track}: ${takenPositions.join(", ")}`
                      : " "
                  }
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>

            {/* Row 4: track + goal_type */}
            <Grid item xs={6}>
              <LabeledField label="Track">
                <FormControl fullWidth size="small">
                  <Select
                    value={form.track ?? "volume"}
                    onChange={set("track")}
                    sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
                  >
                    {TRACKS.map((t) => (
                      <MenuItem key={t} value={t} sx={{ fontSize: "0.83rem" }}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </LabeledField>
            </Grid>
            <Grid item xs={6}>
              <LabeledField label="Goal Type">
                <FormControl fullWidth size="small">
                  <Select
                    value={form.goal_type ?? "total_recipes_cooked"}
                    onChange={set("goal_type")}
                    sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
                  >
                    {GOAL_TYPES.map((t) => (
                      <MenuItem
                        key={t}
                        value={t}
                        sx={{ fontSize: "0.83rem", fontFamily: "monospace" }}
                      >
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </LabeledField>
            </Grid>

            {/* Row 5: target_type + param */}
            <Grid item xs={6}>
              <LabeledField label="Target Type (optional)">
                <TextField
                  fullWidth
                  size="small"
                  value={form.target_type ?? ""}
                  onChange={set("target_type")}
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={6}>
              <LabeledField label="Param (ingredient name / max minutes)">
                <TextField
                  fullWidth
                  size="small"
                  value={form.param ?? ""}
                  onChange={set("param")}
                  placeholder="e.g. onion or 30"
                  sx={fieldSx}
                />
              </LabeledField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditTarget(null)}
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={positionConflict}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#5c6bc0",
              boxShadow: "none",
              "&:hover": { bgcolor: "#4a5ab0", boxShadow: "none" },
            }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminGoals;
